import { useCallback, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { RootState } from "typedefs";

interface UsePaywallOptions {
  returnUrl: string;
  onClose?: () => void;
}

export const usePaywall = ({ returnUrl, onClose }: UsePaywallOptions) => {
  const history = useHistory();
  const { self: user } = useSelector(
    (state: RootState) => state.user,
    shallowEqual,
  );

  const canStartTrial = useMemo(() => {
    const noStatus =
      user?.subscription != null && user?.subscription?.status === "none";
    const hasTrialStarted = !(user?.plan && user?.plan?.trialStartedAt != null);
    return !user?.hasTrialSubscription && noStatus && hasTrialStarted;
  }, [user]);

  const handleUpgrade = useCallback(() => {
    (window as any).location = `/upgrade?return_url=${returnUrl}`;
  }, [returnUrl]);

  const handleStartTrial = useCallback(() => {
    // Athlete-side accounts (athletes, parents/fans) get the analyze trial,
    // everyone else keeps the pro one. The analyze page itself redirects
    // invited athletes away, so no connections lookup is needed here — the
    // rare invited athlete who clicks through sees the page spinner briefly
    // and is bounced home.
    const athleteSide = ["athlete", "other"].includes(
      user?.kind?.toLowerCase(),
    );
    history.push(athleteSide ? "/trial/analyze" : "/trial/pro");
  }, [history, user?.kind]);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  return {
    user,
    canStartTrial,
    handleUpgrade,
    handleStartTrial,
    handleClose,
  };
};

export default usePaywall;
